import { getFontEmbedCSS, toCanvas } from 'html-to-image';
import { useAnimationStore } from '@/stores/animationStore';
import { Model } from '../../../types';
import {
  ExportConfig,
  ExportHandler,
  ExportProgressCallback,
  ExportResult
} from '../exportTypes';
import { beginVisualExport } from '../exportPreparation';
import { createExportMetadata, generateFilename } from '../exportUtils';
import { GifEncoderClient } from '../gifEncoderClient';

const MAX_GIF_EDGE = 1920;
const MAX_GIF_AREA = 2_073_600;
const MAX_GIF_FRAMES = 240;

const shouldIncludeNode = (node: HTMLElement, includeGrid: boolean): boolean => {
  if (!node.classList) return true;
  if (!includeGrid && node.getAttribute('data-grid') === 'true') return false;

  const excludedClasses = [
    'MuiBackdrop-root',
    'MuiDialog-root',
    'transform-control',
    'selection-box',
    'resize-handle',
    'anchor-circle',
    'control-point',
    'connector-handle'
  ];

  if (excludedClasses.some((className) => node.classList.contains(className))) {
    return false;
  }

  if (node.getAttribute('data-element') === 'transform-control') return false;
  if (node.getAttribute('data-element') === 'cursor') return false;
  if (node.getAttribute('data-interaction-layer') === 'true') return false;
  if (node.style.display === 'none' || node.style.visibility === 'hidden') return false;
  return node.style.opacity !== '0';
};

const injectGifExportStyles = (): (() => void) => {
  const style = document.createElement('style');
  style.id = 'flowi-gif-export-cleanup';
  style.textContent = `
    [data-element="transform-control"],
    [data-element="cursor"],
    [data-interaction-layer="true"],
    .transform-control,
    .selection-box,
    .resize-handle,
    .anchor-circle,
    .control-point,
    .connector-handle {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
    }

    [data-testid="isometric-canvas"],
    [data-testid="isometric-canvas"] > div {
      background: transparent !important;
      background-color: transparent !important;
    }
  `;
  document.head.appendChild(style);
  return () => style.remove();
};

const waitForPaint = (): Promise<void> =>
  new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    const timeoutId = window.setTimeout(finish, 50);
    requestAnimationFrame(() => {
      window.clearTimeout(timeoutId);
      finish();
    });
  });

const throwIfAborted = (signal?: AbortSignal): void => {
  if (signal?.aborted) throw new DOMException('GIF export cancelled', 'AbortError');
};

const detectBackgroundColor = (configuredColor?: string): string => {
  if (configuredColor) return configuredColor;
  const dark =
    document.documentElement.classList.contains('dark') ||
    document.body.classList.contains('dark');
  return dark ? '#1e293b' : '#ffffff';
};

const getSafeScale = (width: number, height: number, requestedScale: number): number =>
  Math.min(
    requestedScale,
    MAX_GIF_EDGE / width,
    MAX_GIF_EDGE / height,
    Math.sqrt(MAX_GIF_AREA / (width * height))
  );

const getGifFilename = (configuredFilename: string | undefined, title: string): string => {
  const trimmedFilename = configuredFilename?.trim();
  if (!trimmedFilename) return generateFilename(title || 'flowi-diagram', 'gif');
  return trimmedFilename.toLowerCase().endsWith('.gif')
    ? trimmedFilename
    : `${trimmedFilename}.gif`;
};

export class GIFExportHandler implements ExportHandler {
  public readonly format = 'gif' as const;

  async export(
    element: HTMLElement,
    model: Model,
    config: ExportConfig,
    onProgress?: ExportProgressCallback
  ): Promise<ExportResult> {
    const encoder = new GifEncoderClient();
    const animationActions = useAnimationStore.getState().actions;

    const backgroundColor = detectBackgroundColor(config.backgroundColor);
    const restoreExportState = beginVisualExport(element, backgroundColor);
    const removeStyles = injectGifExportStyles();

    try {
      throwIfAborted(config.abortSignal);
      onProgress?.(0, 'Preparing animated GIF...');

      const width = element.offsetWidth;
      const height = element.offsetHeight;
      if (width <= 0 || height <= 0) {
        throw new Error('Cannot export a canvas with zero dimensions');
      }

      const durationSeconds = config.gifDurationSeconds ?? 3;
      const fps = config.gifFps ?? 12;
      const maxColors = config.gifMaxColors ?? 256;
      const totalFrames = Math.ceil(durationSeconds * fps);
      if (totalFrames > MAX_GIF_FRAMES) {
        throw new Error(`GIF export is limited to ${MAX_GIF_FRAMES} frames`);
      }

      const requestedScale = config.quality ?? 1;
      const scale = getSafeScale(width, height, requestedScale);
      const frameDelayMs = 1000 / fps;
      if (scale < requestedScale) {
        onProgress?.(
          3,
          `Resolution reduced to ${Math.round(scale * 100)}% to keep the GIF within browser limits`
        );
      }

      animationActions.setExportTime(0);
      await waitForPaint();

      let fontEmbedCSS: string | undefined;
      const originalConsoleError = console.error;
      try {
        console.error = (...args: unknown[]) => {
          const message = args.map(String).join(' ');
          if (
            message.includes('Error inlining remote css file') ||
            message.includes('Error while reading CSS rules')
          ) return;
          originalConsoleError(...args);
        };
        fontEmbedCSS = await getFontEmbedCSS(element);
      } catch {
        // html-to-image can still capture with system font fallbacks.
        fontEmbedCSS = undefined;
      } finally {
        console.error = originalConsoleError;
      }

      await encoder.initialize();

      for (let frameIndex = 0; frameIndex < totalFrames; frameIndex += 1) {
        throwIfAborted(config.abortSignal);

        animationActions.setExportTime(frameIndex * frameDelayMs);
        await waitForPaint();

        const progress = 5 + Math.round((frameIndex / totalFrames) * 80);
        onProgress?.(progress, `Rendering GIF frame ${frameIndex + 1} of ${totalFrames}...`);

        const canvas = await toCanvas(element, {
          cacheBust: frameIndex === 0,
          pixelRatio: scale,
          width,
          height,
          backgroundColor,
          fontEmbedCSS,
          skipFonts: fontEmbedCSS === undefined,
          filter: (node: HTMLElement) =>
            shouldIncludeNode(node, config.includeGrid === true),
          style: { transform: 'none' }
        });

        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) throw new Error('Could not read the rendered GIF frame');

        const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
        await encoder.addFrame(
          pixels,
          canvas.width,
          canvas.height,
          frameDelayMs,
          maxColors,
          config.gifLoop === false ? -1 : 0
        );
      }

      throwIfAborted(config.abortSignal);
      onProgress?.(90, 'Encoding animated GIF...');
      const bytes = await encoder.finish();
      const gifBuffer = bytes.buffer.slice(
        bytes.byteOffset,
        bytes.byteOffset + bytes.byteLength
      ) as ArrayBuffer;
      const blob = new Blob([gifBuffer], { type: 'image/gif' });
      const filename = getGifFilename(config.filename, model.title || 'flowi-diagram');
      const metadata = createExportMetadata(
        config,
        Math.round(width * scale),
        Math.round(height * scale),
        model.title
      );

      onProgress?.(100, 'Animated GIF export complete!');
      return { success: true, data: blob, filename, format: 'gif', metadata };
    } catch (error) {
      return {
        success: false,
        filename: '',
        error: error as Error
      };
    } finally {
      animationActions.setExportTime(null);
      encoder.dispose();
      removeStyles();
      restoreExportState();
    }
  }
}

export const createGIFExportHandler = () => new GIFExportHandler();
